import { useRequestBanUser, useRequestGetReports, useRequestIgnoreReport, useRequestWarnUser } from '@/hooks/httpRequest';
import { confirm } from '@/components/custom/react-confirm/Confirm.tsx';
import { notify, notifyInternalMessage } from '@/functions';
import Loader from '@/components/custom/Loader.tsx';
import { PanelInfo } from '@/components/custom';
import type { IReport } from '@/lib/interface.ts';
import { useEffect, useRef, useState } from 'react';
import type { BanTypeEnum } from '@/lib/enum.ts';
import { ThumbsUp } from 'lucide-react';
import ReportItem from '../ReportItem';
import ControlsFiltersSource from '../controlsFilters/ControlsFiltersSource';
import { reportsOfSource } from '../../reportSources';
import { useUrlParams } from '../../hooks';

interface IState {
	reports: IReport[];
}

const ViewDataUsers = () => {

	const { triggerIgnoreReport } = useRequestIgnoreReport({ disableInvalidateQueries: true });
	const { triggerWarnUser } = useRequestWarnUser({ disableInvalidateQueries: true });
	const { triggerBanUser } = useRequestBanUser({ disableInvalidateQueries: true });
	const { reports, isLoadingReports } = useRequestGetReports();
	const [state, setState] = useState<IState>({ reports: [] });
	const hiddenReportIdsRef = useRef<Set<string>>(new Set());
	const { source } = useUrlParams();
	const shownReports = reportsOfSource(state.reports, source);

	useEffect(
		() => {
			const syncedReports = (reports ?? []).filter(
				report => !hiddenReportIdsRef.current.has(report._id)
			);
			setState(l => ({ ...l, reports: syncedReports }));
		},
		[reports]
	);

	const hideReportLocally = (reportId: string) => {
		hiddenReportIdsRef.current.add(reportId);
		setState(
			l => ({
				...l,
				reports: [...l.reports].filter(ll => ll._id !== reportId)
			})
		);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const onWarnReporter = async (reporterUserId: string, reportId: string) => {
		const yes = await confirm('Etes-vous sur de vouloir avertir cet utilisateur ?');

		if (!yes) {
			return;
		}

		triggerWarnUser({ reporterUserId, reportId })
			.then(() => notify(`L'utilisateur a été averti.`))
			.catch(notifyInternalMessage);
	};

	const onWarnReported = async (reportedUserId: string, reportId: string) => {
		const yes = await confirm('Etes-vous sur de vouloir avertir cet utilisateur ?');

		if (!yes) {
			return;
		}

		hideReportLocally(reportId);
		triggerWarnUser({ reportedUserId, reportId })
			.then(() => {
				notify(`L'utilisateur a été averti.`);
			})
			.catch(notifyInternalMessage);
	};

	const onIgnoreReport = async (reportedUserId: string, reportId: string) => {
		const yes = await confirm('Etes-vous sur de vouloir ignorer ce signalement ?');

		if (!yes) {
			return;
		}

		hideReportLocally(reportId);
		triggerIgnoreReport({ userId: reportedUserId, reportId })
			.then(() => {
				notify('Le signalement a été ignoré.');
			})
			.catch(notifyInternalMessage);
	};

	const onBanReportedUser = async (reportedUserId: string, banType: BanTypeEnum, reportId: string) => {
		const yes = await confirm('Etes-vous sur de vouloir bannir cet utilisateur ?');

		if (!yes) {
			return;
		}

		hideReportLocally(reportId);
		triggerBanUser({ userId: reportedUserId, banType })
			.then(() => {
				notify('Le profil a été banni.');
			})
			.catch(notifyInternalMessage);
	};

	if (isLoadingReports) {
		return (
			<div className='my-10'>
				<Loader className='my-10' />
			</div>
		);
	}

	return (
		<div className={'flex flex-col space-y-5'}>
			<ControlsFiltersSource reports={state.reports} />
			{
				shownReports.length === 0
					? (
						<PanelInfo
							title='Aucun signalement disponible'
							description={`Ils se comportent bien. Il n'existe pas de signalements actuellement dans le système.`}
							className='py-10 md:py-26 border-none'
							iconClassName='text-green-500'
							icon={ThumbsUp} />
					)
					: (
						<div className={'flex flex-col items-center space-y-5'}>
							{
								shownReports.map(
									l => (
										<ReportItem
											report={l}
											key={l._id}
											onIgnoreReport={onIgnoreReport}
											onWarnReporter={onWarnReporter}
											onWarnReported={onWarnReported}
											onBanReportedUser={onBanReportedUser} />
									)
								)
							}
						</div>
					)
			}
		</div>
	);

};

export default ViewDataUsers;
